import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row, Spinner, Stack, Badge } from "react-bootstrap";
import ConfirmDelete from "./ConfirmDelete";
import ConfirmBan from "../../../ui/ConfirmBan";
import useApp from "../../../../utils/useApp";
import { UserFields } from "../../../../types/userFields";
import MemberHeader from "./MemberHeader";

interface ViewMemberState {
  user: any;
  activeFamily: any[];
  fetchHousehold: () => void;
  currentCase?: any;
  relationships: any[];
  genders: any[];
  ethnicities: any[];
  races: any[];
  programs: any[];
  jobs: any[];
  rooms: any[];
  beds: any[];
  myCase: boolean;
  isLastUser: boolean;
  resetHouseholdTab: () => void;
  setEditMode: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const ViewMember = ({
  user,
  activeFamily,
  fetchHousehold,
  currentCase,
  relationships,
  genders,
  ethnicities,
  races,
  programs,
  jobs,
  rooms,
  beds,
  myCase,
  isLastUser,
  resetHouseholdTab,
  setEditMode,
  setShowToast,
  setToastText,
  setToastBody,
}: ViewMemberState) => {
  const { auth }: any = useApp();
  const [profileImage, setProfileImage] = useState<string>("");
  const [daysActive, setDaysActive] = useState<string>("0");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBanning, setIsBanning] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userFields, setUserFields] = useState<UserFields>({
    type: "",
    firstName: "",
    lastName: "",
    preferredName: "",
    phone: null,
    intakeDate: undefined,
    exitDate: undefined,
    birthDate: undefined,
    relationship: "",
    gender: "",
    ethnicity: "",
    race: "",
    school: "",
    grade: "",
    program: null,
    level: null,
    job: null,
    room: "",
    bed: null,
    groups: [],
    isPrimary: false,
    isPersonServed: false,
    appAccess: false,
    appAccessLast: undefined,
    isBanned: false,
  });

  useEffect(() => {
    if (!user) return;
    setIsLoading(true);

    const relationship = relationships?.find((r: any) => r.key === user?.relationship) ?? "";
    const gender = genders?.find((g: any) => g.key === user?.gender) ?? "";
    const ethnicity = ethnicities?.find((e: any) => e.key === user?.ethnicity) ?? "";
    const race = races?.find((r: any) => r.key === user?.race) ?? "";
    const selectedProgram = programs.find((p: any) => p.key === user?.program?.program_key);
    let selectedLevel: any = null;
    if (selectedProgram) {
      if (selectedProgram.programLevels && selectedProgram.programLevels.length > 0) {
        if (user?.program?.level_id) {
          selectedLevel = selectedProgram.programLevels.find((lvl: any) => lvl.id === user.program?.level_id);
        }
      }
    }
    const selectedJob = jobs.find((j: any) => j.key === user?.job?.key);
    const selectedRoom = rooms.find((r: any) => r.key === user?.room);
    if (user?.intake_at) {
      const startDate = new Date(user?.intake_at + "T00:00:00");
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const diffTime = today.getTime() - startDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      setDaysActive(diffDays.toString());
    }

    setUserFields({
      type: user.type ?? "",
      firstName: user.firstname ?? "",
      lastName: user.lastname ?? "",
      preferredName: user.preferredname ?? "",
      email: user.email ?? "",
      phone: user.phonenumber === "" ? null : user.phonenumber,
      intakeDate: user.intake_at,
      exitDate: user.exit_at,
      birthDate: user.birthdate,
      relationship: relationship?.name,
      gender: gender?.name,
      ethnicity: ethnicity?.name,
      race: race?.name,
      school: user.school ? user.school : "",
      grade: user.grade ? user.grade : "",
      program: selectedProgram,
      level: selectedLevel ?? null,
      job: selectedJob,
      ...(auth?.profile.includeHMIS === 1 && {
        room: selectedRoom?.name ?? "",
        bed: user.bed ?? null,
      }),
      groups: user.groups ?? [],
      isPrimary: user.is_primary === 1,
      isPersonServed: user.is_case_subject === 1,
      appAccess: user?.appaccess === 1 ? true : false,
      appAccessLast: user.appaccess_last_at,
      isBanned: user.is_banned === 1,
    });

    if (user.profile?.length > 0) {
      user?.profile?.map((p: any) => {
        const base64String = "data:image/jpeg;base64, " + p?.data;
        setProfileImage(base64String);
      });
    } else {
      setProfileImage("");
    }
    setIsLoading(false);
  }, [user, auth]);

  const onClickBan = () => {
    setIsBanning(true);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const formatPhone = (val: string): string => {
    const digits = val?.replace(/\D/g, "").slice(0, 11);
    if (digits?.length < 11) return "";
    return `${digits?.slice(0, 1)} (${digits?.slice(1, 4)}) ${digits?.slice(4, 7)}-${digits?.slice(7)}`;
  };

  const formatDate = (val: string): string => {
    const [year, month, day] = val?.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderMemberData = (label: string, val: string) => (
    <Row className="h-auto ms-1">
      <Col xs={6} lg={4}>
        <h6 className="app-orange-text">{label}</h6>
      </Col>
      <Col xs={6} lg={8}>
        <text className="text-muted">{val}</text>
      </Col>
    </Row>
  );

  return (
    <>
      {isDeleting ? (
        <ConfirmDelete
          user={user}
          family={activeFamily}
          setIsDeleting={setIsDeleting}
          fetchHousehold={fetchHousehold}
          resetHouseholdTab={resetHouseholdTab}
          caseStatus={currentCase?.casestatus}
          isLastUser={isLastUser}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : isBanning ? (
        <ConfirmBan
          user={user}
          previousJob={user?.job?.key ?? ""}
          isBanned={userFields.isBanned ?? false}
          setIsBanning={setIsBanning}
          fetchHousehold={fetchHousehold}
          resetCurrentView={resetHouseholdTab}
          type={user?.type}
          caseStatus={currentCase?.casestatus}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <div>
          <Stack gap={2}>
            <Row>
              <Col xs={8}>
                <MemberHeader user={user} profileImage={profileImage} />
              </Col>
              {!user?.deleted_at && (
                <Col xs={4}>
                  <Stack className="stack-float" direction="horizontal" gap={3}>
                    {myCase && (
                      <>
                        {(auth?.profile?.role === "org_admin" || auth?.profile?.role === "admin") && (
                          <>
                            <Button className="app-danger-color-inverse button" onClick={() => onClickBan()}>
                              {userFields.isBanned ? "Unban" : "Ban"}
                            </Button>
                            <div className="vr" />
                          </>
                        )}
                        <Button className="app-danger-color button" onClick={() => onClickDelete()}>
                          Delete
                        </Button>
                        <div className="vr" />
                        <Button className="app-secondary-color button" onClick={() => setEditMode((prev: any) => !prev)}>
                          Edit
                        </Button>
                      </>
                    )}
                  </Stack>
                </Col>
              )}
            </Row>
            <Row className="m-0">
              {userFields?.exitDate ? (
                <>
                  <Col xs={6} className="p-0">
                    <Stack direction="horizontal" gap={2}>
                      <h6 className="app-orange-text mb-0">{"Exit"}</h6>
                      <text className="text-muted">{formatDate(userFields?.exitDate)}</text>
                    </Stack>
                  </Col>
                </>
              ) : (
                userFields?.intakeDate && (
                  <>
                    <Col xs={6} className="p-0">
                      <Stack direction="horizontal" gap={2}>
                        <h6 className="app-orange-text mb-0">{"Intake"}</h6>
                        <text className="text-muted">{formatDate(userFields?.intakeDate)}</text>
                        <text className="text-muted">{`(${daysActive} days)`}</text>
                      </Stack>
                    </Col>
                  </>
                )
              )}
              <Col xs={6} className="text-end p-0">
                <small className="text-muted">{user?.number_infractions + " Infraction(s)"}</small>
              </Col>
            </Row>

            <h5 className="app-header-text mb-0">Demographics</h5>
            <hr className="mt-0 mb-2" />
            <Stack gap={1}>
              {user?.type === "guardian" && renderMemberData("Email", userFields?.email ? userFields?.email : "-")}
              {renderMemberData("Phone", userFields?.phone ? formatPhone(userFields?.phone) : "-")}
              {renderMemberData("Birth Date", userFields?.birthDate ? formatDate(userFields?.birthDate) : "-")}
              {renderMemberData("Gender", userFields?.gender ?? "-")}
              {renderMemberData("Ethnicity", userFields?.ethnicity ?? "-")}
              {renderMemberData("Race", userFields?.race ?? "-")}
              {user?.relationship === "child" && renderMemberData("School", userFields?.school ? userFields?.school : "-")}
              {user?.relationship === "child" && renderMemberData("Grade", userFields?.grade ? userFields?.grade : "-")}
            </Stack>

            {user?.customfields?.length > 0 && (
              <>
                <h5 className="app-header-text mb-0">Custom Fields</h5>
                <hr className="mt-0 mb-2" />
                <Stack gap={1}>
                  {user?.customfields?.map((c: any) =>
                    c?.type === "date"
                      ? renderMemberData(c.label, c.value ? formatDate(c.value) : "-")
                      : c?.type === "checkbox"
                        ? renderMemberData(c.label, c.value && c.value === "1" ? "true" : "false")
                        : renderMemberData(c.label, c.value ? c.value : "-"),
                  )}
                </Stack>
              </>
            )}

            <h5 className="app-header-text mb-0">Program and Assignment</h5>
            <hr className="mt-0 mb-2" />
            <Stack gap={1}>
              {programs?.length > 0 &&
                renderMemberData(
                  "Program & Level",
                  [userFields?.program?.name, userFields?.level?.name].filter(Boolean).join(" - ") || "-",
                )}
              {rooms?.length > 0 &&
                beds?.length > 0 &&
                renderMemberData("Room & Bed", [userFields?.room, userFields?.bed?.name].filter(Boolean).join(" - ") || "-")}
              {jobs?.length > 0 &&
                renderMemberData(
                  "Department & Job",
                  [userFields?.job?.department, userFields?.job?.name].filter(Boolean).join(" - ") || "-",
                )}
            </Stack>
            {userFields.groups &&
              userFields.groups?.length > 0 &&
              userFields.groups.map((g: any, index: number) => renderMemberData(index === 0 ? "Groups" : "", g?.name))}
          </Stack>
        </div>
      )}
    </>
  );
};

export default ViewMember;
